/**
 * High-Performance Client-Side Timezone Engine
 * Uses pre-compiled native C++ Intl.DateTimeFormat instances with memoization
 * Formats thousands of dates in single-digit milliseconds with 0 server load.
 */

const formatterCache = new Map();

/**
 * Retrieves or initializes a memoized native Intl.DateTimeFormat instance
 * @param {string} timeZone IANA timezone e.g. "America/New_York"
 * @param {Object} [options] 
 * @param {string} [locale="en-US"] 
 * @returns {Intl.DateTimeFormat}
 */
export function getCachedFormatter(timeZone = 'UTC', options = {}, locale = 'en-US') {
    const tz = timeZone || 'UTC';
    const key = `${locale}__${tz}__${JSON.stringify(options)}`;
    if (!formatterCache.has(key)) {
        try {
            formatterCache.set(key, new Intl.DateTimeFormat(locale, { timeZone: tz, ...options }));
        } catch {
            // Fallback if invalid timezone passed
            formatterCache.set(key, new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options }));
        }
    }
    return formatterCache.get(key);
}

/**
 * Normalizes input date/string to Date object
 * @param {Date|string|number} value 
 * @returns {Date|null}
 */
export function parseDate(value) {
    if (!value || String(value).startsWith('0000-00-00')) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const text = String(value).trim();
    const isoText = text.includes('T') ? text : text.replace(' ', 'T');
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(isoText);
    const date = new Date(hasTimezone ? isoText : `${isoText}Z`);

    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats an on-screen datetime in the specified timezone
 * @param {Date|string|number} value 
 * @param {string} timeZone 
 * @returns {string} e.g. "15 Oct 2026, 10:30 AM"
 */
export function formatDisplayDateTime(value, timeZone = 'UTC') {
    const date = parseDate(value);
    if (!date) return '-';

    const fmt = getCachedFormatter(timeZone, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }, 'en-GB');

    return fmt.format(date);
}

/**
 * Formats on-screen time only
 * @param {Date|string|number} value 
 * @param {string} timeZone 
 * @returns {string} e.g. "10:30 AM"
 */
export function formatDisplayTime(value, timeZone = 'UTC') {
    const date = parseDate(value);
    if (!date) return '-';

    const fmt = getCachedFormatter(timeZone, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }, 'en-US');

    return fmt.format(date);
}

/**
 * Formats on-screen date only
 * @param {Date|string|number} value 
 * @param {string} timeZone 
 * @returns {string} e.g. "15 Oct 2026"
 */
export function formatDisplayDate(value, timeZone = 'UTC') {
    const date = parseDate(value);
    if (!date) return '-';

    const fmt = getCachedFormatter(timeZone, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }, 'en-GB');

    return fmt.format(date);
}

/**
 * Fixed, unambiguous date-time formatter for Excel & CSV exports.
 * Uses en-GB (DD-MM-YYYY) or en-CA (YYYY-MM-DD) independent of viewer OS locale.
 * @param {Date|string|number} value 
 * @param {string} timeZone 
 * @returns {string} e.g. "15-10-2026 10:30 AM"
 */
export function formatExportDateTime(value, timeZone = 'UTC') {
    const date = parseDate(value);
    if (!date) return '';

    const fmt = getCachedFormatter(timeZone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }, 'en-GB');

    const parts = fmt.formatToParts(date).reduce((acc, p) => {
        acc[p.type] = p.value;
        return acc;
    }, {});

    const dayPeriod = String(parts.dayPeriod || '').toUpperCase();
    return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute} ${dayPeriod}`.trim();
}

/**
 * Batch formats date field for an array of rows
 * @param {Array<Object>} rows 
 * @param {string} dateKey 
 * @param {string} timeZone 
 * @returns {Array<Object>}
 */
export function formatBatchDates(rows = [], dateKey, timeZone = 'UTC') {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const fmt = getCachedFormatter(timeZone, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }, 'en-GB');

    return rows.map((row) => {
        const val = row[dateKey];
        const date = parseDate(val);
        return {
            ...row,
            [`${dateKey}_formatted`]: date ? fmt.format(date) : '',
        };
    });
}

/**
 * Returns the short timezone abbreviation for a given timezone
 * @param {string} timeZone 
 * @param {Date} [date] 
 * @returns {string}
 */
export function getTimezoneAbbr(timeZone = 'UTC', date = new Date()) {
    try {
        const fmt = getCachedFormatter(timeZone, { timeZoneName: 'short' });
        const parts = fmt.formatToParts(date);
        const tzPart = parts.find((p) => p.type === 'timeZoneName');
        return tzPart ? tzPart.value : '';
    } catch {
        return '';
    }
}

/**
 * Returns current Date object with hours/mins/secs matching current wall-clock in target timezone
 * @param {string} timeZone
 * @returns {Date}
 */
export function getNowInTimezone(timeZone = 'UTC') {
    try {
        const fmt = getCachedFormatter(timeZone, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }, 'en-US');
        const parts = fmt.formatToParts(new Date()).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});
        const hour = parts.hour === '24' ? 0 : parseInt(parts.hour, 10);
        return new Date(
            parseInt(parts.year, 10),
            parseInt(parts.month, 10) - 1,
            parseInt(parts.day, 10),
            hour,
            parseInt(parts.minute, 10),
            parseInt(parts.second, 10),
            0
        );
    } catch {
        return new Date();
    }
}

/**
 * Checks if a given Date matches the calendar day "today" in target timezone
 * @param {Date} date
 * @param {string} timeZone
 * @returns {boolean}
 */
export function isTodayInTimezone(date, timeZone = 'UTC') {
    if (!date) return false;
    const nowTz = getNowInTimezone(timeZone);
    return (
        date.getFullYear() === nowTz.getFullYear() &&
        date.getMonth() === nowTz.getMonth() &&
        date.getDate() === nowTz.getDate()
    );
}

/**
 * Resolves the viewer's PC timezone dynamically using both Intl and system clock offset.
 * @returns {string} IANA timezone name
 */
export function getBrowserTimezone() {
    try {
        const offsetMinutes = new Date().getTimezoneOffset(); // Difference in minutes between UTC and local

        if (offsetMinutes === -120) return "Etc/GMT-2"; // UTC+2
        if (offsetMinutes === -180) return "Etc/GMT-3"; // UTC+3
        if (offsetMinutes === -210) return "Asia/Tehran"; // UTC+3:30
        if (offsetMinutes === -270) return "Asia/Kabul"; // UTC+4:30
        if (offsetMinutes === -330) return "Asia/Kolkata"; // UTC+5:30
        if (offsetMinutes === -345) return "Asia/Kathmandu"; // UTC+5:45
        if (offsetMinutes === -360) return "Asia/Dhaka"; // UTC+6
        if (offsetMinutes === -390) return "Asia/Yangon"; // UTC+6:30
        if (offsetMinutes === -420) return "Asia/Bangkok"; // UTC+7
        if (offsetMinutes === -480) return "Asia/Singapore"; // UTC+8
        if (offsetMinutes === -540) return "Asia/Tokyo"; // UTC+9
        if (offsetMinutes === -570) return "Australia/Adelaide"; // UTC+9:30
        if (offsetMinutes === -600) return "Australia/Sydney"; // UTC+10
        if (offsetMinutes === 0) return "UTC";

        const intlTz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
        if (intlTz && intlTz !== "UTC") {
            return intlTz;
        }

        if (offsetMinutes !== undefined && !Number.isNaN(offsetMinutes)) {
            const hours = -offsetMinutes / 60;
            if (Number.isInteger(hours)) {
                return hours > 0 ? `Etc/GMT-${hours}` : `Etc/GMT+${Math.abs(hours)}`;
            }
        }

        return intlTz || "UTC";
    } catch {
        return "UTC";
    }
}

/**
 * Resolves the true UTC Date instant for an event's date, time and venue timezone.
 * @param {string|Date} dateStr e.g. "2026-09-08"
 * @param {string} [timeStr="00:00:00"] e.g. "09:30:00"
 * @param {string} [tz="Asia/Kolkata"] e.g. "Asia/Kolkata"
 * @returns {Date|null}
 */
export function computeEventUtcInstant(dateStr, timeStr = "00:00:00", tz = "Asia/Kolkata") {
    if (!dateStr) return null;
    try {
        const rawDate = String(dateStr).slice(0, 10);
        const rawTime = String(timeStr || "00:00:00").slice(0, 8);
        const isoString = `${rawDate}T${rawTime}Z`;
        const initialUtc = new Date(isoString);
        if (Number.isNaN(initialUtc.getTime())) return null;

        const targetTz = tz || "Asia/Kolkata";
        const parts = getCachedFormatter(targetTz, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }, "en-US").formatToParts(initialUtc).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});

        const tzYear = parseInt(parts.year, 10);
        const tzMonth = parseInt(parts.month, 10) - 1;
        const tzDay = parseInt(parts.day, 10);
        const tzHour = parts.hour === "24" ? 0 : parseInt(parts.hour, 10);
        const tzMin = parseInt(parts.minute, 10);
        const tzSec = parseInt(parts.second, 10);

        const tzTime = Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMin, tzSec);
        const offsetMs = tzTime - initialUtc.getTime();
        return new Date(initialUtc.getTime() - offsetMs);
    } catch {
        return parseDate(dateStr);
    }
}

/**
 * Extracts start and end UTC Date instances from an event object.
 * Checks start_at_utc / end_at_utc first; otherwise computes dynamically.
 * @param {Object} eventData
 * @returns {{ startUtc: Date|null, endUtc: Date|null }}
 */
export function getEventStartEndUtc(eventData) {
    if (!eventData) return { startUtc: null, endUtc: null };

    let startUtc = null;
    if (eventData.start_at_utc) {
        startUtc = parseDate(eventData.start_at_utc);
    }
    if (!startUtc && eventData.start_date) {
        startUtc = computeEventUtcInstant(
            eventData.start_date,
            eventData.start_time,
            eventData.timezone || "Asia/Kolkata"
        );
    }

    let endUtc = null;
    if (eventData.end_at_utc) {
        endUtc = parseDate(eventData.end_at_utc);
    }
    if (!endUtc && (eventData.end_date || eventData.start_date)) {
        endUtc = computeEventUtcInstant(
            eventData.end_date || eventData.start_date,
            eventData.end_time || eventData.start_time || "23:59:59",
            eventData.timezone || "Asia/Kolkata"
        );
    }

    return { startUtc, endUtc };
}

/**
 * Formats event date range adjusted to target timezone (defaults to viewer's PC timezone).
 * Handles day roll-overs across international date lines.
 * @param {Object} eventData
 * @param {string} [targetTz]
 * @returns {string} e.g. "8 - 9 Sept 2026" or "08 Sept 2026"
 */
export function formatPublicEventDate(eventData, targetTz) {
    if (!eventData?.start_date && !eventData?.start_at_utc) return "Date not set";
    const tz = targetTz || getBrowserTimezone();
    const { startUtc, endUtc } = getEventStartEndUtc(eventData);

    if (!startUtc) return "Date not set";
    if (!endUtc) return formatDisplayDate(startUtc, tz);

    const startFmt = getCachedFormatter(tz, { day: "numeric", month: "short", year: "numeric" }, "en-GB");
    const endFmt = getCachedFormatter(tz, { day: "numeric", month: "short", year: "numeric" }, "en-GB");

    const startParts = startFmt.formatToParts(startUtc).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const endParts = endFmt.formatToParts(endUtc).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});

    const sameDay = startParts.day === endParts.day && startParts.month === endParts.month && startParts.year === endParts.year;
    const sameMonth = startParts.month === endParts.month && startParts.year === endParts.year;
    const sameYear = startParts.year === endParts.year;

    if (sameDay) {
        return `${startParts.day} ${startParts.month} ${startParts.year}`;
    }
    if (sameMonth) {
        return `${startParts.day} - ${endParts.day} ${endParts.month} ${endParts.year}`;
    }
    if (sameYear) {
        return `${startParts.day} ${startParts.month} - ${endParts.day} ${endParts.month} ${endParts.year}`;
    }
    return `${startParts.day} ${startParts.month} ${startParts.year} - ${endParts.day} ${endParts.month} ${endParts.year}`;
}

/**
 * Formats event start time adjusted to target timezone (defaults to viewer's PC timezone).
 * @param {Object} eventData
 * @param {string} [targetTz]
 * @returns {string} e.g. "09:30 AM", "11:30 AM"
 */
export function formatPublicEventTime(eventData, targetTz) {
    if (!eventData?.start_time && !eventData?.start_at_utc) return "";
    const tz = targetTz || getBrowserTimezone();
    const { startUtc } = getEventStartEndUtc(eventData);
    if (!startUtc) return "";
    return formatDisplayTime(startUtc, tz);
}

/**
 * Formats event end time adjusted to target timezone (defaults to viewer's PC timezone).
 * @param {Object} eventData
 * @param {string} [targetTz]
 * @returns {string} e.g. "06:00 PM"
 */
export function formatPublicEventEndTime(eventData, targetTz) {
    if (!eventData?.end_time && !eventData?.end_at_utc) return "";
    const tz = targetTz || getBrowserTimezone();
    const { endUtc } = getEventStartEndUtc(eventData);
    if (!endUtc) return "";
    return formatDisplayTime(endUtc, tz);
}

/**
 * Formats a single event date (start or end) in short weekday/month format (e.g. "Tue, Sep 8")
 * in the target timezone (defaults to viewer's PC timezone).
 * @param {Date|string} dateInstant
 * @param {string} [targetTz]
 * @returns {string} e.g. "Tue, Sep 8"
 */
export function formatPublicEventDateShort(dateInstant, targetTz) {
    if (!dateInstant) return "TBA";
    const date = parseDate(dateInstant);
    if (!date) return "TBA";
    const tz = targetTz || getBrowserTimezone();
    const fmt = getCachedFormatter(tz, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }, 'en-US');
    return fmt.format(date);
}

/**
 * Resolves countdown target timestamp in epoch milliseconds for an event.
 * @param {Object} eventData
 * @returns {number|null}
 */
export function getEventTargetTs(eventData) {
    if (!eventData) return null;
    const { startUtc } = getEventStartEndUtc(eventData);
    return startUtc ? startUtc.getTime() : null;
}

/**
 * Formats a Date/timestamp into a specific timezone with standard tokens
 * @param {Date|string|number} date
 * @param {string} [timeZone]
 * @param {'YYYY-MM-DD'|'HH:mm:ss'|'HH:mm'|'display'} [pattern='display']
 * @returns {string}
 */
export function formatInTimezone(date, timeZone = 'UTC', pattern = 'display') {
    const d = parseDate(date);
    if (!d) return '';
    const tz = timeZone || 'UTC';
    if (pattern === 'YYYY-MM-DD') {
        const fmt = getCachedFormatter(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA');
        return fmt.format(d);
    }
    if (pattern === 'HH:mm:ss') {
        const fmt = getCachedFormatter(tz, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }, 'en-GB');
        return fmt.format(d);
    }
    if (pattern === 'HH:mm') {
        const fmt = getCachedFormatter(tz, { hour: '2-digit', minute: '2-digit', hour12: false }, 'en-GB');
        return fmt.format(d);
    }
    return formatDisplayDateTime(d, tz);
}

/**
 * Converts event start and end to the viewer's local device schedule
 * @param {Object} eventData
 * @param {string} [viewerTz]
 * @returns {{ startUtc: Date|null, endUtc: Date|null, startDate: string, endDate: string, startTime: string, endTime: string }}
 */
export function toLocalEventSchedule(eventData, viewerTz) {
    if (!eventData) return { startUtc: null, endUtc: null, startDate: '', endDate: '', startTime: '', endTime: '' };
    const { startUtc, endUtc } = getEventStartEndUtc(eventData);
    const localTz = viewerTz || getBrowserTimezone();
    return {
        startUtc,
        endUtc,
        startDate: startUtc ? formatInTimezone(startUtc, localTz, 'YYYY-MM-DD') : (eventData.start_date || ''),
        endDate: endUtc ? formatInTimezone(endUtc, localTz, 'YYYY-MM-DD') : (eventData.end_date || ''),
        startTime: startUtc ? formatInTimezone(startUtc, localTz, 'HH:mm:ss') : (eventData.start_time || '00:00:00'),
        endTime: endUtc ? formatInTimezone(endUtc, localTz, 'HH:mm:ss') : (eventData.end_time || '23:59:59'),
    };
}

/**
 * Curated list of popular global timezones grouped by region
 */
export const POPULAR_TIMEZONES = [
    { group: 'India & South Asia', zones: [
        { label: 'India Standard Time (IST, UTC+5:30)', value: 'Asia/Kolkata' },
        { label: 'Sri Lanka Time (UTC+5:30)', value: 'Asia/Colombo' },
        { label: 'Bangladesh Time (UTC+6:00)', value: 'Asia/Dhaka' },
        { label: 'Nepal Time (UTC+5:45)', value: 'Asia/Kathmandu' },
        { label: 'Pakistan Time (PKT, UTC+5:00)', value: 'Asia/Karachi' },
    ]},
    { group: 'North America', zones: [
        { label: 'Eastern Time - New York, Miami, Toronto (EDT/EST, UTC-4/-5)', value: 'America/New_York' },
        { label: 'Central Time - Chicago, Dallas, Winnipeg (CDT/CST, UTC-5/-6)', value: 'America/Chicago' },
        { label: 'Mountain Time - Denver, Calgary (MDT/MST, UTC-6/-7)', value: 'America/Denver' },
        { label: 'Pacific Time - Los Angeles, San Francisco, Vancouver (PDT/PST, UTC-7/-8)', value: 'America/Los_Angeles' },
        { label: 'Alaska Time (AKDT/AKST, UTC-8/-9)', value: 'America/Anchorage' },
        { label: 'Hawaii Time (HST, UTC-10)', value: 'Pacific/Honolulu' },
    ]},
    { group: 'Europe & UK', zones: [
        { label: 'United Kingdom - London, Edinburgh (BST/GMT, UTC+1/+0)', value: 'Europe/London' },
        { label: 'Central European - Paris, Berlin, Rome, Amsterdam (CEST/CET, UTC+2/+1)', value: 'Europe/Paris' },
        { label: 'Eastern European - Athens, Bucharest, Helsinki (EEST/EET, UTC+3/+2)', value: 'Europe/Athens' },
        { label: 'Western European - Lisbon, Dublin (WEST/WET, UTC+1/+0)', value: 'Europe/Lisbon' },
        { label: 'Moscow Time (MSK, UTC+3)', value: 'Europe/Moscow' },
    ]},
    { group: 'Middle East', zones: [
        { label: 'Gulf Standard Time - Dubai, Abu Dhabi, Muscat (GST, UTC+4)', value: 'Asia/Dubai' },
        { label: 'Arabian Standard Time - Riyadh, Doha, Kuwait (AST, UTC+3)', value: 'Asia/Riyadh' },
        { label: 'Israel Time - Jerusalem, Tel Aviv (IDT/IST, UTC+3/+2)', value: 'Asia/Jerusalem' },
    ]},
    { group: 'East & Southeast Asia', zones: [
        { label: 'Singapore, Malaysia - Singapore, Kuala Lumpur (SGT/MYT, UTC+8)', value: 'Asia/Singapore' },
        { label: 'China, Hong Kong - Beijing, Shanghai, Hong Kong (CST/HKT, UTC+8)', value: 'Asia/Hong_Kong' },
        { label: 'Japan, Korea - Tokyo, Seoul (JST/KST, UTC+9)', value: 'Asia/Tokyo' },
        { label: 'Thailand, Vietnam, Indonesia (West) - Bangkok, Hanoi, Jakarta (ICT/WIB, UTC+7)', value: 'Asia/Bangkok' },
        { label: 'Philippines - Manila (PHT, UTC+8)', value: 'Asia/Manila' },
    ]},
    { group: 'Australia & Pacific', zones: [
        { label: 'Australian Eastern - Sydney, Melbourne, Brisbane (AEST/AEDT, UTC+10/+11)', value: 'Australia/Sydney' },
        { label: 'Australian Central - Adelaide (ACST/ACDT, UTC+9:30/+10:30)', value: 'Australia/Adelaide' },
        { label: 'Australian Western - Perth (AWST, UTC+8)', value: 'Australia/Perth' },
        { label: 'New Zealand - Auckland, Wellington (NZST/NZDT, UTC+12/+13)', value: 'Pacific/Auckland' },
    ]},
    { group: 'Universal', zones: [
        { label: 'Coordinated Universal Time (UTC)', value: 'UTC' },
    ]},
];

